/* ══════════════════════════════════════════════════════════════
   DEFCON Tracker — Headline-derived global conflict monitor
   Uses recent GDELT conflict headlines instead of canned alerts
   ══════════════════════════════════════════════════════════════ */

import { renderUnavailableBlock } from './data-state';

interface MilitaryAlert {
  time: string;
  icon: string;
  text: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  source: string;
  link: string;
}

interface DEFCONState {
  level: number;
  label: string;
  description: string;
  alerts: MilitaryAlert[];
}

interface GDELTArticle {
  title?: string;
  url?: string;
  source?: string;
  domain?: string;
  seendate?: string;
  tone?: number;
}

let defconRefreshTimer: ReturnType<typeof setInterval> | null = null;
let currentRequestId = 0;

const DEFCON_LABELS: Record<number, { label: string; desc: string }> = {
  1: { label: 'COCKED PISTOL', desc: 'Extreme escalation across multiple conflict signals' },
  2: { label: 'FAST PACE', desc: 'Severe conflict headlines dominating the current feed' },
  3: { label: 'ROUND HOUSE', desc: 'Elevated conflict posture across recent headlines' },
  4: { label: 'DOUBLE TAKE', desc: 'Above-normal geopolitical stress in live reporting' },
  5: { label: 'FADE OUT', desc: 'No unusual concentration of live conflict headlines' },
};

function getSeverityColor(severity: MilitaryAlert['severity']): string {
  switch (severity) {
    case 'critical': return '#ff0000';
    case 'high': return '#ff6600';
    case 'medium': return '#ffaa00';
    case 'low': return '#00cc44';
    default: return '#888';
  }
}

function classifySeverity(title: string, tone: number): MilitaryAlert['severity'] {
  const text = title.toLowerCase();
  if (/(nuclear|ballistic|icbm|missile launch|airstrike|invasion|warship attack)/.test(text) || tone <= -8) {
    return 'critical';
  }
  if (/(missile|drone attack|troops|navy|military|strike|border clash|cyberattack|shelling)/.test(text) || tone <= -5) {
    return 'high';
  }
  if (/(defense|security|sanction|warning|exercise|ceasefire|conflict)/.test(text) || tone <= -2) {
    return 'medium';
  }
  return 'low';
}

function iconForSeverity(severity: MilitaryAlert['severity']): string {
  switch (severity) {
    case 'critical': return '🚨';
    case 'high': return '⚠️';
    case 'medium': return '🛰️';
    case 'low': return '📝';
    default: return '•';
  }
}

function formatSeenTime(dateStr?: string): string {
  if (!dateStr) return 'recent';

  const match = dateStr.match(/^(\d{4})(\d{2})(\d{2})T?(\d{2})?(\d{2})?/);
  if (!match) return 'recent';

  const [, year, month, day, hour = '00', minute = '00'] = match;
  const parsed = new Date(`${year}-${month}-${day}T${hour}:${minute}:00Z`);
  if (Number.isNaN(parsed.getTime())) return 'recent';

  return parsed.toLocaleTimeString('en-US', {
    timeZone: 'UTC',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }) + ' UTC';
}

function computeLevel(alerts: MilitaryAlert[]): number {
  const critical = alerts.filter(alert => alert.severity === 'critical').length;
  const high = alerts.filter(alert => alert.severity === 'high').length;
  const medium = alerts.filter(alert => alert.severity === 'medium').length;

  if (critical >= 3 || (critical >= 2 && high >= 2)) return 1;
  if (critical >= 1 || high >= 5) return 2;
  if (high >= 2 || medium >= 5) return 3;
  if (medium >= 2 || alerts.length >= 4) return 4;
  return 5;
}

async function fetchDEFCONState(): Promise<DEFCONState | null> {
  const query = '"military" OR missile OR troops OR attack OR conflict OR defense OR cyberattack';
  const url = `https://api.gdeltproject.org/api/v2/doc/doc?query=${encodeURIComponent(query)}&mode=ArtList&maxrecords=12&format=json&timespan=12h`;

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`GDELT API responded with ${response.status}`);
  }

  const data = await response.json() as { articles?: GDELTArticle[] };
  const articles = Array.isArray(data.articles) ? data.articles : [];
  if (articles.length === 0) return null;

  const alerts = articles
    .filter(article => article.title)
    .slice(0, 10)
    .map(article => {
      const severity = classifySeverity(article.title || '', article.tone || 0);
      return {
        time: formatSeenTime(article.seendate),
        icon: iconForSeverity(severity),
        text: article.title || 'Untitled headline',
        severity,
        source: article.source || article.domain || 'GDELT',
        link: article.url || '#',
      };
    });

  if (alerts.length === 0) return null;

  const level = computeLevel(alerts);
  const info = DEFCON_LABELS[level];
  return {
    level,
    label: info.label,
    description: info.desc,
    alerts,
  };
}

function renderState(container: HTMLElement, state: DEFCONState): void {
  const levelColors: Record<number, string> = {
    1: '#ff0000',
    2: '#ff4400',
    3: '#ffaa00',
    4: '#3388ff',
    5: '#00ff41',
  };
  const color = levelColors[state.level] || '#ffaa00';

  const alertsHtml = state.alerts.map(alert => `
    <a class="wm-defcon__alert" href="${alert.link}" target="_blank" rel="noreferrer noopener">
      <span class="wm-defcon__alert-time">${alert.time}</span>
      <span class="wm-defcon__alert-icon">${alert.icon}</span>
      <span style="color: ${getSeverityColor(alert.severity)}">${alert.text}</span>
      <span class="wm-defcon__alert-time">${alert.source}</span>
    </a>
  `).join('');

  container.innerHTML = `
    <div class="wm-defcon wm-defcon--${state.level}">
      <div class="wm-defcon__ring">
        <svg class="wm-defcon__ring-svg" viewBox="0 0 100 100">
          <circle class="wm-defcon__ring-circle" cx="50" cy="50" r="46" stroke="${color}" opacity="0.3"/>
          <circle class="wm-defcon__ring-circle" cx="50" cy="50" r="40" stroke="${color}" opacity="0.5"/>
          <circle class="wm-defcon__ring-circle" cx="50" cy="50" r="34" stroke="${color}" opacity="0.7"/>
        </svg>
        <div class="wm-defcon__level" style="color:${color}">${state.level}</div>
      </div>
      <div class="wm-defcon__label" style="color:${color}">DEFCON ${state.level}</div>
      <div class="wm-defcon__desc">${state.label} • ${state.description}</div>
      <div class="wm-defcon__alerts">
        ${alertsHtml}
      </div>
      <div class="wm-defcon__desc" style="margin-top:0.75rem;">Derived from live GDELT conflict headlines.</div>
    </div>
  `;
}

async function loadDEFCON(container: HTMLElement): Promise<void> {
  const requestId = ++currentRequestId;

  try {
    const state = await fetchDEFCONState();
    if (requestId !== currentRequestId) return;

    if (!state) {
      container.innerHTML = renderUnavailableBlock('wm-empty', 'Live conflict monitor unavailable');
      return;
    }

    renderState(container, state);
  } catch {
    if (requestId !== currentRequestId) return;
    container.innerHTML = renderUnavailableBlock('wm-empty', 'Live conflict monitor unavailable');
  }
}

export function renderDEFCONTracker(container: HTMLElement): void {
  container.innerHTML = renderUnavailableBlock('wm-empty', 'Loading live conflict monitor...');
  void loadDEFCON(container);

  defconRefreshTimer = setInterval(() => {
    void loadDEFCON(container);
  }, 300000);
}

export function destroyDEFCONTracker(): void {
  if (defconRefreshTimer) clearInterval(defconRefreshTimer);
  defconRefreshTimer = null;
  currentRequestId += 1;
}
